import React, { useState, memo, useCallback, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './TaskList.module.scss';
import classNames from 'classnames/bind';
import BaseTable from '~/components/common/base-table/BaseTable';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import MenuOption from '~/components/customer/menu-option/MenuOption';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { handleGetTaskOfCustomerList } from '~/thunks/task/taskThunk';
import { handleApiError } from '~/utils/helper';
import { ITask } from '~/utils/interface/task';
import { BaseImage } from '~/components/common';
import { taskActions } from '~/thunks/task/taskSlice';
import StatusLabelTask from '~/components/customer/status-label-task/StatusLabelTask';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';

const cx = classNames.bind(styles);

type TaskListProps = {
  id: string;
  onOpenEdit: (id: string) => void;
};

function TaskList(props: TaskListProps) {
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  const { t } = useTranslation('admin/contact');
  const headCells: Array<HeadCelType> = useMemo(
    () => [
      {
        id: 'name',
        label: t(`properties.taskName`),
        sortable: true,
        searchable: true,
      },
      {
        id: 'dueDate',
        label: t(`properties.dueDate`),
        sortable: true,
        searchable: true,
      },
      {
        id: 'assigneeName',
        label: t(`properties.assignedTo`),
        sortable: true,
        searchable: true,
      },
      {
        id: 'customerName',
        label: t(`properties.customerName`),
        sortable: true,
        searchable: true,
      },
      {
        id: 'description',
        label: t(`properties.description`),
        sortable: true,
        searchable: true,
      },
      {
        id: 'status',
        label: t(`properties.status`),
        sortable: true,
        searchable: true,
      },
      {
        id: '',
        label: '',
        sortable: false,
        searchable: false,
      },
    ],
    [t]
  );
  const { filterTaskContactList, currentPage, totalPages, needRefreshData } = useAppSelector((state) => state.task);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectId, setSelectId] = useState<string>('');
  const [taskList, setTaskList] = useState<Array<ITask>>([]);

  useEffect(() => {
    const handleOpenEdit = async () => {
      try {
        const response = await dispatch(
          handleGetTaskOfCustomerList({ taskId: props.id, data: filterTaskContactList })
        ).unwrap();
        setTaskList(response.responses);
      } catch (e) {
        handleApiError(e);
      }
    };
    handleOpenEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id, filterTaskContactList, needRefreshData]);

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectId(itemId);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(taskActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleEditItem = () => {
    handleClose();
    props.onOpenEdit(selectId);
  };

  const handleDeleteItem = () => {
    handleClose();
    if (selectId !== undefined) {
      dispatch(taskActions.changeMenuSelectId(selectId));
      dispatch(taskActions.changeOpenModalDelete(true));
    }
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      taskActions.setFilterTaskContactList({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      taskActions.setFilterTaskContactList({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };

  return (
    <Box className={cx('task-list-container')}>
      <BaseTable<ITask>
        data={taskList}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        sort={filterTaskContactList.sort}
        totalPages={totalPages}
        renderCell={(task: ITask) => {
          return (
            <>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('taskName')}>{task.name}</Box>
              </TableCell>

              <TableCell>
                <Box className={cx('dueDate')}>{task?.dueDate ? dayjs(task.dueDate).format('DD MMM, YYYY') : ''}</Box>
              </TableCell>

              <TableCell>
                <Box className={cx('task-container')}>
                  {task.assignees?.[0]?.avatar && <BaseImage src={task.assignees[0].avatar} className={cx('image')} />}
                  <Box className={cx('task-name-container')}>
                    <Box className={cx('taskAssignee')}>{task.assignees?.[0]?.name}</Box>
                  </Box>
                </Box>
              </TableCell>

              <TableCell>
                <Box className={cx('organization')}>{task.customerName}</Box>
              </TableCell>

              <TableCell>
                <Box className={cx('description-container')}>{task.description}</Box>
              </TableCell>

              <TableCell>
                <Box className={cx('status-container')}>
                  {task?.status && <StatusLabelTask variant={task.statusDetail} />}
                </Box>
              </TableCell>

              {(permissions[PERMISSIONS.contactTask_edit] || permissions[PERMISSIONS.contactTask_delete]) && (
                <TableCell>
                  <Box className={cx('action-container')}>
                    <IconButton
                      onClick={(event) => {
                        openOptionMenu(event, task.id);
                      }}
                    >
                      <MenuVerticalIcon className='w-[19px] h-[19px]' />
                    </IconButton>
                  </Box>
                </TableCell>
              )}
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        enableEdit={permissions[PERMISSIONS.contactTask_edit]}
        enableDelete={permissions[PERMISSIONS.contactTask_delete]}
      />
    </Box>
  );
}

export default memo(TaskList);
